import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Button,
  Divider,
  Grid,
  Typography,
  useTheme,
  Box,
  Dialog,
  DialogContent,
  useMediaQuery,
} from "@mui/material";
import { Link, useHistory } from "react-router-dom";

import SecondaryCenterTemplate from "components/SecondaryCenterTemplate";
import {
  Field,
  reduxForm,
  getFormSyncErrors,
  getFormValues,
  change,
} from "redux-form";
import { renderTextField } from "components/FormFields/FieldTypes";
import { phone as phoneNormalizer } from "components/FormFields/normalizers";
import { phone as phoneFormatter } from "components/FormFields/formatters";
import {
  Required,
  emailValidator,
  phone as phoneValidator,
} from "components/FormFields/validators";
import { homeActions } from "actions";
import SuccessDialog from "components/SuccessDialog";
import { Helmet } from "react-helmet";

const SignUp = (props) => {
  const theme = useTheme();
  const matchesMinWidth = useMediaQuery("(max-width:600px)");

  const history = useHistory();
  const {
    formErrors,
    formValues,
    signUp,
    signUpInfo,
    signUpParams,
    resetSignUpStatus,
    change,
    updateGTMDataLayer,
    gtmInfo,
    organizationInfo,
  } = props;

  const [inputType, setInputType] = useState("phone");
  const [showSignupSuccess, setShowSignupSuccess] = useState(false);
  const [showSignupFailure, setShowSignupFailure] = useState(false);
  const [showSignUpFinalSuccess, setShowSignUpFinalSuccess] = useState(false);
  const tagManagerArgs = {
    dataLayer: gtmInfo,
    dataLayerName: "PageDataLayer",
  };

  const onFormSubmit = () => {
    signUp(formValues);
  };

  useEffect(() => {
    props.setValue(6);
    resetSignUpStatus();
    document.getElementById("appcontent").scroll(0, 0);
    updateGTMDataLayer({
      page: "Global",
      screen: "Sign Up",
      screen_title: "Global",
      event: "Nav_sign_up",
    });
    return () => {
      updateGTMDataLayer({
        screen_referrer: "Sign Up",
        page: "Global",
        screen: "Sign Up",
        screen_title: "Global",
        event: "Nav_sign_up",
      });
    };
  }, []);

  useEffect(() => {
    window.dataLayer.push({
      ...tagManagerArgs.dataLayer,
    });
  }, [gtmInfo]);

  useEffect(() => {
    if (
      signUpInfo &&
      Object.keys(signUpInfo) &&
      Object.keys(signUpInfo).length
    ) {
      if (signUpInfo.success) {
        setShowSignupSuccess(true);
      } else {
        setShowSignupFailure(true);
      }
    }
  }, [signUpInfo]);

  return (
    <SecondaryCenterTemplate>
      <Helmet>
        <title>
          Sign Up |{" "}
          {organizationInfo?.app?.appCode === "CVD"
            ? "Cuverd® Partner"
            : "Cuverd®"}
        </title>
        <meta
          name="description"
          content="Sign up now to find savings and discount coupons on your prescription medications with Cuverd®."
        />
        <meta property="og:title" content="Sign Up | Cuverd®" />
        <meta
          property="og:description"
          content="Sign up now to find savings and discount coupons on your prescription medications with Cuverd®."
        />
        <meta
          property="og:image"
          content="assets/images/cuverd-logo-header.svg"
        />
        <link
          rel="alternate"
          href="https://www.cuverd.com/sign-up"
          hrefLang="en-us"
        />
        <link rel="canonical" href="https://www.cuverd.com/sign-up" />
      </Helmet>
      <Grid container>
        <Grid item sm={8} sx={{ margin: "auto" }}>
          <Box sx={{ height: "100%", overflowY: "auto" }} pb={6}>
            <Box mb={2} sx={{ textAlign: "center" }}>
              <Typography
                variant={matchesMinWidth ? "h2LatoHeavy" : "h1LatoHeavy"}
              >
                Sign up is quick and easy
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }} mb={7}>
              <Typography variant="h3LatoRegular">
                {inputType === "phone"
                  ? "Please enter a phone number that can receive text messages:"
                  : "Please enter your email address:"}
              </Typography>
            </Box>
            <Grid item container alignItems="center" justifyContent="center">
              <Grid
                item
                xs={12}
                id={inputType === "phone" ? "mobileRegister" : "emailRegister"}
              >
                {inputType === "phone" ? (
                  <Field
                    name="phone"
                    isRequired
                    component={renderTextField}
                    label="Mobile Phone Number"
                    format={phoneFormatter}
                    validate={[Required, phoneValidator]}
                    normalize={phoneNormalizer}
                    handleKeyDown={(e) => {
                      if (
                        e.key &&
                        e.key == "Enter" &&
                        Object.keys(formErrors).length == 0
                      ) {
                        onFormSubmit();
                      }
                    }}
                  />
                ) : (
                  <Field
                    name="email"
                    isRequired
                    component={renderTextField}
                    label="Email Address"
                    validate={[Required, emailValidator]}
                    handleKeyDown={(e) => {
                      if (
                        e.key &&
                        e.key == "Enter" &&
                        Object.keys(formErrors).length == 0
                      ) {
                        onFormSubmit();
                      }
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <Box my={"51px"}>
                  <Divider
                    sx={{
                      "&:before": {
                        borderColor: theme.palette.primary.main,
                      },
                      "&:after": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Typography variant="h2LatoBold">OR</Typography>
                  </Divider>
                </Box>
              </Grid>
              <Grid item xs={12} mb={10} sx={{ textAlign: "center" }}>
                <Button
                  variant="largeOutline"
                  type="button"
                  id={inputType === "phone" ? "useEmailBtn" : "useMobileBtn"}
                  sx={{ width: "100%", minHeight: "48px", minWidth: 60 }}
                  onClick={() => {
                    if (inputType === "phone") {
                      setInputType("email");
                      change("phone", "");
                    } else {
                      setInputType("phone");
                      change("email", "");
                    }
                  }}
                >
                  {inputType === "phone"
                    ? "USE MY EMAIL ADDRESS"
                    : "USE MY PHONE NUMBER"}
                </Button>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                sx={{
                  margin: "auto",
                  marginBottom: "24px",
                  textAlign: "center",
                }}
              >
                <Box>
                  <Typography variant="h4LatoRegular">
                    By continuing to use this website, you agree to the Cuverd®
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h3LatoRegular"
                    sx={{ color: theme.palette.common.greenFour }}
                    component={Link}
                    to="/terms-and-conditions"
                    onClick={() => resetSignUpStatus()}
                  >
                    Terms and Conditions
                  </Typography>
                  <Typography variant="h4LatoRegular">
                    &nbsp;and&nbsp;
                  </Typography>
                  <Typography
                    variant="h3LatoRegular"
                    sx={{ color: theme.palette.common.greenFour }}
                    component={Link}
                    to="/privacy-policy"
                    onClick={() => resetSignUpStatus()}
                  >
                    Privacy Policy
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} mb={4}>
                <Box
                  sx={{ textAlign: "center" }}
                  component={Link}
                  to="/SignIn"
                  onClick={() => resetSignUpStatus()}
                >
                  <Typography
                    variant="h5LatoBlack"
                    sx={{
                      color: theme.palette.common.greenFour,
                      textDecoration: "underline",
                    }}
                  >
                    Sign in if you already have an account
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} mb={4} sx={{ textAlign: "center" }}>
                <Button
                  variant="large"
                  type="button"
                  onClick={() => onFormSubmit()}
                  disabled={Object.keys(formErrors).length ? true : false}
                  sx={{
                    width: "100%",
                    minHeight: "48px",
                    minWidth: 60,
                    // backgroundColor: Object.keys(formErrors).length
                    //   ? "#e6e6e6"
                    //   : undefined,
                    // color: Object.keys(formErrors).length
                    //   ? "#637276"
                    //   : undefined,
                    boxShadow: "none",
                  }}
                >
                  sign up
                </Button>
              </Grid>
            </Grid>
          </Box>
          {showSignupSuccess ? (
            <Dialog open={showSignupSuccess} className="info-dialog">
              <DialogContent sx={{ padding: "20px" }}>
                <Box mt={2}>
                  <Typography
                    sx={{
                      fontSize: matchesMinWidth ? "12px" : "1rem",
                      wordBreak: "break-word",
                    }}
                    variant="text4"
                  >
                    {signUpInfo.message}
                  </Typography>
                </Box>
                <Box mt={1} mb={3}>
                  <Typography
                    variant="h3LatoBlack"
                    sx={{
                      fontSize: matchesMinWidth ? "16px" : "24px",
                      wordBreak: "break-word",
                    }}
                  >
                    {signUpParams &&
                      (signUpParams.email ||
                        phoneFormatter(signUpParams.phone))}
                  </Typography>
                </Box>
                <Box sx={{ paddingTop: "32px" }}>
                  <Typography variant="text4">
                    Please use it to continue signing in
                  </Typography>
                </Box>
                <Box mt={3} mb={3}>
                  <Button
                    variant="large"
                    onClick={() => {
                      history.push("/VerifyPassword");
                      setShowSignupSuccess(false);
                    }}
                    sx={{ minWidth: { xs: "100%", sm: "100%", md: undefined } }}
                  >
                    CONTINUE
                  </Button>
                </Box>
                <Box mt={3}>
                  <Button
                    variant="text"
                    onClick={() => {
                      setShowSignupSuccess(false);
                    }}
                    sx={{ minWidth: { xs: "100%", sm: "100%", md: undefined } }}
                  >
                    CANCEL
                  </Button>
                </Box>
              </DialogContent>
            </Dialog>
          ) : null}
          {showSignupFailure ? (
            <Dialog open={showSignupFailure} className="info-dialog">
              <DialogContent sx={{ padding: "20px" }}>
                <Box mt={2}>
                  <Typography
                    sx={{
                      fontSize: matchesMinWidth ? "12px" : "1rem",
                      wordBreak: "break-word",
                    }}
                    variant="text4"
                  >
                    {signUpInfo.message}
                  </Typography>
                </Box>
                <Box mt={1} mb={3}>
                  <Typography
                    variant="h3LatoBlack"
                    sx={{
                      fontSize: matchesMinWidth ? "16px" : "24px",
                      wordBreak: "break-word",
                    }}
                  >
                    {signUpParams &&
                      (signUpParams.email ||
                        phoneFormatter(signUpParams.phone))}
                  </Typography>
                </Box>
                <Box mt={3} mb={3}>
                  <Button
                    variant="large"
                    onClick={() => {
                      setShowSignupFailure(false);
                    }}
                    sx={{ minWidth: { xs: "100%", sm: "100%", md: undefined } }}
                  >
                    Try Again
                  </Button>
                </Box>
              </DialogContent>
            </Dialog>
          ) : null}
          {showSignUpFinalSuccess ? (
            <SuccessDialog
              showSuccessDialog={showSignUpFinalSuccess}
              successMsg={"successMsg" || ""}
              onSuccessClick={() => {
                setShowSignUpFinalSuccess(false);
              }}
            />
          ) : null}
        </Grid>
      </Grid>
    </SecondaryCenterTemplate>
  );
};

const mapStateToProps = (state) => {
  return {
    formErrors: getFormSyncErrors("SIGNUP_FORM")(state) || {},
    formValues: getFormValues("SIGNUP_FORM")(state),
    signUpInfo: state.signUp.data || {},
    signUpParams: state.signUp.signUpParams,
    gtmInfo: state.gtmInfo.data,
    organizationInfo: state.organizationInfo.data,
  };
};

const mapDispatchToProps = {
  signUp: (params) => homeActions.signUp(params),
  resetSignUpStatus: () => homeActions.resetSignUpStatus(),
  updateGTMDataLayer: (param) => homeActions.updateGTMDataLayer(param),
  change,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "SIGNUP_FORM",
  })(SignUp)
);
